/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as Msdyn365 from '@msdyn365-commerce/core';

import {
    IAddressAddItem,
    IAddressAddUpdateProps,
    IAddressList,
    IAddressListItem,
    IAddressListProps,
    IAddressShowItem,
    IAddressShowProps,
    IAccountManagementAddressViewProps
} from '@msdyn365-commerce-modules/address';

import { IAccountManagementAddressConfig, IAccountManagementAddressResources } from '../definition-extensions/account-management-address.ext.props.autogenerated';
import { BackButtonIcon } from '../../../components/icons';

/**
 * Address show component.
 * @param param0 - Root param.
 * @param param0.AddressDetail - Address detail.
 * @param param0.items - IAddressShowItem[].
 * @returns - Address Node.
 */
const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items.map((item: IAddressShowItem) => {
                return <>{item.description}</>;
            })}
        </Node>
    );
};

/**
 * Address List Component.
 * @param param0 - Root param.
 * @param param0.List - Address list.
 * @param param0.isShowList - Boolean.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressListItem[].
 * @returns - AddressListComponent Node.
 */
const AddressList: React.FC<IAddressList & { config: IAccountManagementAddressConfig }> = ({ config, List, isShowList, heading, items }) => {
    if (!isShowList) {
        return null;
    }

    return (
        <Node {...List}>
            {heading}
            {items.map((item: IAddressListItem) => {
                const { Item, key, error, showItems, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                const { Error, isShowError, title, message } = error;
                return (
                    <Node {...Item} key={key}>
                        <AddressShow {...showItems} />
                        {isShowError && (
                            <Node {...Error}>
                                {title}
                                {message}
                            </Node>
                        )}
                        {isShowPrimaryButton && primaryButton}
                        {config.allowEditing && editButton}
                        {config.allowEditing && removeButton}
                    </Node>
                );
            })}
        </Node>
    );
};

/**
 * Address Lists Component.
 * @param param0 - Root param.
 * @param param0.ListAddress - List address.
 * @param param0.heading - List heading.
 * @param param0.isShowEmptyListMessage - Boolean.
 * @param param0.emptyListMessage - Empty list message.
 * @param param0.addButton - Add button.
 * @param param0.primaryAddressList - Primary address.
 * @param param0.otherAddressList - Other address.
 * @returns Address Lists Component Node.
 */
const AddressLists: React.FC<IAddressListProps & { config: IAccountManagementAddressConfig }> = ({
    config,
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList
}) => {
    return (
        <Node {...ListAddress}>
            {heading}
            {isShowEmptyListMessage && emptyListMessage}
            {config.allowEditing && addButton}
            <AddressList config={config} {...primaryAddressList} />
            <AddressList config={config} {...otherAddressList} />
        </Node>
    );
};

/**
 * AddressAddUpdateComponent.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - Error message.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns Address add update component node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton
}) => {
    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                return (
                    <Node {...AddressItem} key={key}>
                        {label}
                        {alert}
                        {input}
                    </Node>
                );
            })}
            {error.title && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const BackButton: React.FC<{ context: Msdyn365.ICoreContext, resources: IAccountManagementAddressResources }> = props => {
    const { context, resources } = props;
    const baseClassName = 'ms-account-management-address';
    const accountPageUrl = Msdyn365.getUrlSync('account', context.actionContext);

    return (
        <a className={`${baseClassName}__back-button`} href={accountPageUrl}>
            <BackButtonIcon />
            <span>
                {resources.backButtonLabel}
            </span>
        </a>
    );
};

/**
 * Account Management Address View Component.
 * @param props - Props.
 * @returns - AccountAddressManagement Module.
 */
const AccountManagementAddressView: React.FC<IAccountManagementAddressViewProps> = props => {
    const {
        AccountAddressManagement,
        // @ts-expect-error -- Removed because every information message mentions editing addresses but we don't allow that
        infoMessageBar,
        viewState,
        showAddressList,
        showAddOrUpdateAddress,
        screenReaderNotification
    } = props;
    const config = (props as any).config as IAccountManagementAddressConfig;
    const resources = (props as any).resources as IAccountManagementAddressResources;

    const addressType = showAddOrUpdateAddress.items.find(item => item.key === 'AddressTypeValue');
    const newAddressTypeInput = React.cloneElement((addressType?.input as React.ReactElement), {
        // Remove all address types except for Delivery and Invoice
        displayData: (addressType?.input as React.ReactElement).props.displayData.filter((item: any) => item.key === 1 || item.key === 2)
    });
    addressType!.input = newAddressTypeInput;

    // Force React to update it's state to reflect the new address type values.
    // Otherwise the address type value will not update to the new top value after the filtering above.
    const orgSaveOnClick = (showAddOrUpdateAddress.saveButton as React.ReactElement).props.onClick;
    const newSaveButton = React.cloneElement((showAddOrUpdateAddress.saveButton as React.ReactElement), {
        onClick: () => {
            document.getElementById('shipping_addressaddresstypevalue')!.dispatchEvent(new Event('change', { bubbles: true }));
            orgSaveOnClick();
        }
    });
    showAddOrUpdateAddress.saveButton = newSaveButton;

    return (
        <Module {...AccountAddressManagement}>
            <BackButton context={props.context} resources={resources} />
            <Msdyn365.Text
                className='ms-account-management-address__info'
                text={resources.addressEditingInformationText}
            />
            {viewState.isShowAddresList && <AddressLists config={config} {...showAddressList} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} />}
            {screenReaderNotification}
        </Module>
    );
};

export default AccountManagementAddressView;
